/**
 * Volume Delta (Estimated)
 *
 * Estimates buying vs selling volume using candle body position
 * within the total range. Bullish candles assign more volume to
 * buying, bearish to selling.
 *
 * Delta = BuyVolume − SellVolume
 *
 * Category: Volume | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const volumeDeltaIndicator: IndicatorDefinition = {
  id: "volume_delta",
  name: "Volume Delta",
  shortName: "VDelta",
  category: "volume",
  pane: "separate",
  params: [
    { key: "smoothing", label: "Smoothing", type: "number", default: 1, min: 1, max: 20 },
  ],
  plots: [
    { key: "delta", label: "Delta", color: "#2196F3", style: "histogram", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { smoothing } = params;
    const rawDelta: number[] = [];

    for (let i = 0; i < candles.length; i++) {
      const c = candles[i];
      const range = c.high - c.low;
      const vol = c.volume || 0;

      if (range === 0) {
        rawDelta.push(0);
      } else {
        // Estimate buy/sell ratio from candle body position
        const bodyMid = (c.open + c.close) / 2;
        const rangeMid = (c.high + c.low) / 2;
        const buyRatio = 0.5 + (bodyMid - rangeMid) / range;
        const clampedRatio = Math.max(0, Math.min(1, buyRatio));
        const buyVol = vol * clampedRatio;
        const sellVol = vol * (1 - clampedRatio);
        rawDelta.push(buyVol - sellVol);
      }
    }

    // Apply SMA smoothing if > 1
    const deltaData: { time: number; value: number }[] = [];

    if (smoothing <= 1) {
      for (let i = 0; i < candles.length; i++) {
        deltaData.push({ time: candles[i].time, value: rawDelta[i] });
      }
    } else {
      for (let i = smoothing - 1; i < candles.length; i++) {
        let sum = 0;
        for (let j = i - smoothing + 1; j <= i; j++) {
          sum += rawDelta[j];
        }
        deltaData.push({ time: candles[i].time, value: sum / smoothing });
      }
    }

    return [{ key: "delta", data: deltaData }];
  },
};
